import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import {
  Bot, Phone, Settings, Zap, Code, ArrowLeft, Save, Trash2,
  PhoneIncoming, PhoneOutgoing, Play, Download, ChevronRight, AlertTriangle
} from 'lucide-react';
import { LANGUAGE_OPTIONS, TIMEZONE_OPTIONS } from '../constants/agentOptions';

const TABS = ['Overview', 'Configuration', 'Call History', 'Actions', 'Embed'];

// Типы post-call действий и порядок их вкладок — зеркало ACTION_TYPES в
// backend/app/domain/calls/post_call_actions.py. Все они могут быть включены
// одновременно, поэтому это вкладки с чекбоксом, а не выпадающий список.
const ACTION_TYPES = [
  { id: 'email', label: 'Email' },
  { id: 'sms_team', label: 'SMS to team' },
  { id: 'sms_caller', label: 'SMS to caller' },
  { id: 'webhook', label: 'Webhook' },
];

const WEBHOOK_FIELDS = [
  'caller_number', 'transcript', 'summary', 'duration', 'recording_url', 'timestamp',
];

const SMS_MAX_CHARS = 160;

// Значения и дефолты настроек embed-виджета — зеркало
// backend/app/domain/agents/embed.py. Порядок в списках задаёт порядок в выпадашках.
const EMBED_THEMES = ['light', 'dark'];
const EMBED_STATES = ['collapsed', 'expanded'];
const EMBED_POSITIONS = ['fixed', 'static'];
const EMBED_VISUALIZER_COLORS = ['verdant', 'twilight', 'bloom', 'mystic', 'flare', 'glacier'];
const EMBED_MAX_TEXT = 60;

const EMBED_DEFAULTS = {
  enabled: false,
  theme: 'dark',
  default_state: 'collapsed',
  position: 'fixed',
  start_call_text: "Let's chat",
  agent_thinking_text: 'Thinking...',
  speak_to_interrupt_text: 'Speak to interrupt',
  visualizer_color: 'verdant',
  logo_icon_url: '',
};

const actionLabel = (id) => ACTION_TYPES.find(t => t.id === id)?.label || id;

export default function AgentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [agent, setAgent] = useState(null);
  const [calls, setCalls] = useState([]);
  const [selectedCall, setSelectedCall] = useState(null);
  const [tab, setTab] = useState('Overview');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [telnyxLive, setTelnyxLive] = useState(true);
  const [freeDids, setFreeDids] = useState([]);
  const [selectedDid, setSelectedDid] = useState('');
  const [numBusy, setNumBusy] = useState(false);
  const [voices, setVoices] = useState([]);

  useEffect(() => {
    loadAgent();
    loadCalls();
  }, [id]);

  useEffect(() => {
    api.get('/agents/meta/voices')
      .then(res => setVoices(res.data.voices || []))
      .catch(() => {});
  }, []);

  const loadAgent = async () => {
    try {
      const res = await api.get(`/agents/${id}`);
      setAgent(res.data.agent);
      setEditForm(res.data.agent);
      setTelnyxLive(res.data.telnyx_live !== false);
      if (res.data.agent.type === 'inbound') loadFreeDids();
    } catch {
      toast.error('Agent not found');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadCalls = async () => {
    try {
      const res = await api.get(`/calls?agent_id=${id}&limit=50`);
      setCalls(res.data.calls || []);
    } catch {}
  };

  const loadFreeDids = async () => {
    try {
      const res = await api.get('/agents/meta/dids');
      setFreeDids(res.data.dids || []);
    } catch (err) {
      console.warn('[loadFreeDids]', err);
      toast.error('Could not load available numbers');
    }
  };

  const addNumber = async () => {
    if (!selectedDid) return toast.error('Select a number to add');
    const d = freeDids.find(x => x.id === selectedDid);
    if (!d) return;
    setNumBusy(true);
    try {
      const res = await api.post(`/agents/${id}/numbers`, { did_id: d.id, did_number: d.number });
      setAgent(a => ({ ...a, phone_numbers: res.data.phone_numbers }));
      setSelectedDid('');
      loadFreeDids();
      toast.success('Number added');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add number');
    } finally {
      setNumBusy(false);
    }
  };

  const removeNumber = async (numberId) => {
    if (!window.confirm('Remove this phone number from the agent?')) return;
    setNumBusy(true);
    try {
      const res = await api.delete(`/agents/${id}/numbers/${numberId}`);
      setAgent(a => ({ ...a, phone_numbers: res.data.phone_numbers }));
      loadFreeDids();
      toast.success('Number removed');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to remove number');
    } finally {
      setNumBusy(false);
    }
  };

  const saveAgent = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/agents/${id}`, editForm);
      setAgent(res.data.agent);
      toast.success('Agent saved!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const deleteAgent = async () => {
    if (!window.confirm(`Delete agent "${agent.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/agents/${id}`);
      toast.success('Agent deleted');
      navigate('/dashboard');
    } catch (err) {
      const msg = err.response?.data?.error || 'Delete failed';
      toast.error(msg);
    }
  };

  const clearHistory = async () => {
    if (!window.confirm('Clear all call history for this agent? This cannot be undone.')) return;
    try {
      await api.delete(`/calls/agent/${id}`);
      setCalls([]);
      toast.success('Call history cleared');
    } catch {
      toast.error('Failed to clear history');
    }
  };

  const upd = (k, v) => setEditForm(f => ({ ...f, [k]: v }));

  if (loading) return <div className="page-loading"><div className="spinner" /></div>;

  const Icon = agent.type === 'inbound' ? PhoneIncoming : PhoneOutgoing;
  const transcriptMessages = (selectedCall?.transcript || []).filter(
    msg => ['assistant', 'agent', 'user', 'caller'].includes(msg.role) && msg.text?.trim()
  );

  return (
    <div className="page">
      <div className="page-header">
        <button className="btn btn-ghost" onClick={() => navigate('/dashboard')}>
          <ArrowLeft size={16} /> Back
        </button>
        <div className="agent-title">
          <div className="agent-avatar-sm"><Bot size={20} /></div>
          <div>
            <h1>{agent.name}</h1>
            <span className={`badge badge--${agent.type}`}><Icon size={12} /> {agent.type}</span>
          </div>
        </div>
        <div className="header-actions">
          <button className="btn btn-danger-ghost" onClick={deleteAgent}>
            <Trash2 size={16} /> Delete
          </button>
          <button className="btn btn-primary" onClick={saveAgent} disabled={saving}>
            <Save size={16} /> {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Telnyx sync warning */}
      {!telnyxLive && agent.telnyx_agent_id && (
        <div className="banner banner--warning">
          <AlertTriangle size={16} />
          <span>Unable to reach Telnyx. Showing cached data — some fields may be outdated.</span>
        </div>
      )}

      {/* Tabs */}
      <div className="tabs">
        {TABS.map(t => (
          <button key={t} className={`tab ${tab === t ? 'tab--active' : ''}`} onClick={() => setTab(t)}>
            {t}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {tab === 'Overview' && (
        <div className="tab-content">
          <div className="overview-stats">
            <div className="stat-card">
              <Phone size={20} />
              <span className="stat-value">{agent.call_count}</span>
              <span className="stat-label">Total Calls</span>
            </div>
            <div className="stat-card">
              <span className="stat-value">{(agent.total_minutes || 0).toFixed(1)}</span>
              <span className="stat-label">Minutes</span>
            </div>
            <div className="stat-card">
              <span className="stat-value" style={{ color: agent.status === 'active' ? '#22c55e' : '#6b7280' }}>
                {agent.status}
              </span>
              <span className="stat-label">Status</span>
            </div>
          </div>

          <div className="info-grid">
            <div className="info-item"><label>Phone Number</label><span>{agent.phone_number || '—'}</span></div>
            <div className="info-item"><label>Language</label><span>{agent.language}</span></div>
            <div className="info-item"><label>Voice</label><span>{voices.find(v => v.id === agent.voice)?.name || agent.voice || '—'}</span></div>
            <div className="info-item"><label>Timezone</label><span>{agent.timezone || '—'}</span></div>
            <div className="info-item"><label>Recording</label><span>{agent.recording_enabled ? '✅ On' : '❌ Off'}</span></div>
            <div className="info-item">
              <label>Post-call Actions</label>
              <span>
                {ACTION_TYPES.filter(t => agent.post_call_actions?.[t.id]?.enabled)
                  .map(t => t.label).join(', ') || '—'}
              </span>
            </div>
          </div>

          {agent.type === 'inbound' && (
            <div className="form-section" style={{ marginTop: 24 }}>
              <h3>Phone Numbers</h3>
              <p className="helper-text">All numbers ring the same assistant. The last number can only be removed by deleting the agent.</p>
              <div className="numbers-list">
                {(agent.phone_numbers || []).map(n => (
                  <div key={n.id} className="number-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                    <span><Phone size={14} /> {n.phone_number}{n.status === 'failed' ? ' ⚠️' : ''}</span>
                    <button
                      className="btn btn-sm btn-danger-ghost"
                      disabled={numBusy || (agent.phone_numbers || []).length <= 1}
                      title={(agent.phone_numbers || []).length <= 1 ? 'To remove the last number, delete the agent' : 'Remove number'}
                      onClick={() => removeNumber(n.id)}
                    >
                      <Trash2 size={14} /> Remove
                    </button>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <select className="input" value={selectedDid} onChange={e => setSelectedDid(e.target.value)} style={{ flex: 1 }}>
                  <option value="">— select an available number —</option>
                  {freeDids.map(d => <option key={d.id} value={d.id}>{d.number}</option>)}
                </select>
                <button className="btn btn-primary" onClick={addNumber} disabled={numBusy || !selectedDid}>
                  Add number
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Configuration Tab */}
      {tab === 'Configuration' && (
        <div className="tab-content form-container">
          <div className="form-section">
            <h3>Agent Settings</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Agent Name</label>
                <input className="input" value={editForm.name || ''} onChange={e => upd('name', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Voice</label>
                <select className="input" value={editForm.voice || ''} onChange={e => upd('voice', e.target.value)}>
                  {/* Keep the agent's current value selectable even if it's a legacy
                      voice no longer returned by Telnyx (e.g. old Polly.* ids). */}
                  {editForm.voice && !voices.some(v => v.id === editForm.voice) && (
                    <option value={editForm.voice}>{editForm.voice}</option>
                  )}
                  {voices.map(v => (
                    <option key={v.id} value={v.id}>{v.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Language</label>
                <select className="input" value={editForm.language || ''} onChange={e => upd('language', e.target.value)}>
                  {!LANGUAGE_OPTIONS.some(language => language.value === editForm.language) && (
                    <option value={editForm.language || ''}>
                      {editForm.language === 'auto'
                        ? 'Auto-detect (legacy)'
                        : editForm.language ? `${editForm.language} (legacy)` : '— not set (legacy) —'}
                    </option>
                  )}
                  {LANGUAGE_OPTIONS.map(language => (
                    <option key={language.value} value={language.value}>{language.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Timezone</label>
                <select className="input" value={editForm.timezone || ''} onChange={e => upd('timezone', e.target.value)}>
                  <option value="">— not set —</option>
                  {TIMEZONE_OPTIONS.map(timezone => (
                    <option key={timezone} value={timezone}>{timezone}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>System Instructions</label>
              <textarea className="input textarea" rows={6}
                value={editForm.system_prompt || ''}
                onChange={e => upd('system_prompt', e.target.value)}
                placeholder="How should this agent behave? What should it say? What info to collect?" />
            </div>

            <div className="form-group">
              <label>Welcome Message</label>
              <div className="toggle-row">
                <span>Enable</span>
                <input type="checkbox" checked={!!editForm.welcome_message_enabled}
                  onChange={e => upd('welcome_message_enabled', e.target.checked)} />
              </div>
              {editForm.welcome_message_enabled && (
                <textarea className="input textarea" rows={2} style={{ marginTop: 8 }}
                  value={editForm.welcome_message_text || ''}
                  onChange={e => upd('welcome_message_text', e.target.value)} />
              )}
            </div>

            <div className="toggle-row">
              <label>Call Recording</label>
              <input type="checkbox" checked={!!editForm.recording_enabled}
                onChange={e => upd('recording_enabled', e.target.checked)} />
            </div>
          </div>
        </div>
      )}

      {/* Call History Tab - Messenger style */}
      {tab === 'Call History' && (
        <div className="tab-content">
          <div className="messenger-layout">
            {/* Left: Call list */}
            <div className="call-list">
              <div className="call-list-header">
                <span>{calls.length} calls</span>
                <button className="btn btn-sm btn-danger-ghost" onClick={clearHistory}>Clear</button>
              </div>
              {calls.length === 0 ? (
                <div className="empty-state-sm">No calls yet</div>
              ) : calls.map(call => (
                <div
                  key={call.id}
                  className={`call-item ${selectedCall?.id === call.id ? 'call-item--active' : ''}`}
                  onClick={() => setSelectedCall(call)}
                >
                  <div className="call-item__icon">
                    {call.direction === 'inbound' ? <PhoneIncoming size={14} /> : <PhoneOutgoing size={14} />}
                  </div>
                  <div className="call-item__info">
                    <span className="call-number" title={call.caller_number || 'Unknown'}>{call.caller_number || 'Unknown'}</span>
                    <span className="call-time">{new Date(call.started_at * 1000).toLocaleString()}</span>
                  </div>
                  <div className="call-item__meta">
                    <span className="call-duration">{Math.floor((call.duration_seconds || 0) / 60)}m {(call.duration_seconds || 0) % 60}s</span>
                    <span className={`status-dot status-dot--${call.status}`} />
                  </div>
                </div>
              ))}
            </div>

            {/* Center: Transcript */}
            <div className="transcript-panel">
              {!selectedCall ? (
                <div className="empty-state">Select a call to view transcript</div>
              ) : (
                <>
                  <div className="transcript-header">
                    <strong>{selectedCall.caller_number}</strong>
                    <span>{new Date(selectedCall.started_at * 1000).toLocaleString()}</span>
                    <span>{Math.floor(selectedCall.duration_seconds / 60)}m {selectedCall.duration_seconds % 60}s</span>
                  </div>

                  <div className="transcript-body">
                    {transcriptMessages.length > 0 ? (
                      transcriptMessages.map((msg, i) => (
                        <div key={i} className={`transcript-msg transcript-msg--${msg.role === 'assistant' || msg.role === 'agent' ? 'agent' : 'caller'}`}>
                          <span className="msg-role">{msg.role === 'assistant' || msg.role === 'agent' ? agent.name : 'Caller'}</span>
                          <p>{msg.text}</p>
                        </div>
                      ))
                    ) : (
                      <div className="empty-state-sm">No transcript available</div>
                    )}
                  </div>

                  {selectedCall.recording_url && (
                    <div className="recording-bar">
                      <Play size={16} />
                      <audio controls src={selectedCall.recording_url} style={{ flex: 1 }} />
                      <a href={selectedCall.recording_url} download className="btn btn-sm">
                        <Download size={14} />
                      </a>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Right: Call details */}
            {selectedCall && (
              <div className="call-details-panel">
                <h4>Call Details</h4>
                <div className="detail-item"><label>Status</label><span>{selectedCall.status}</span></div>
                <div className="detail-item"><label>Direction</label><span>{selectedCall.direction}</span></div>
                <div className="detail-item"><label>Duration</label><span>{selectedCall.duration_seconds}s</span></div>
                <div className="detail-item"><label>Cost</label><span>${(selectedCall.cost || 0).toFixed(4)}</span></div>

                {selectedCall.post_call_action_status && (
                  <div className="post-call-action">
                    <h5>Post-Call Actions</h5>
                    <span className={`status-badge status-badge--${selectedCall.post_call_action_status}`}>
                      {selectedCall.post_call_action_status}
                    </span>
                    {(selectedCall.post_call_action_result?.actions || []).map(row => (
                      <div key={row.action} className="detail-item">
                        <label>{actionLabel(row.action)}</label>
                        <span
                          className={`status-badge status-badge--${row.status}`}
                          title={row.error || row.reason || row.to || row.url || ''}
                        >
                          {row.status}{row.reason ? ` (${row.reason})` : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Actions Tab */}
      {tab === 'Actions' && (
        <ActionTab agent={editForm} upd={upd} onSave={saveAgent} saving={saving} />
      )}

      {/* Embed Tab */}
      {tab === 'Embed' && (
        <EmbedTab agent={editForm} upd={upd} onSave={saveAgent} saving={saving} />
      )}
    </div>
  );
}

function ActionTab({ agent, upd, onSave, saving }) {
  const actions = agent.post_call_actions || {};
  const [current, setCurrent] = useState(ACTION_TYPES[0].id);

  const config = actions[current] || {};
  // Настройки каждого действия лежат в своей ветке post_call_actions, поэтому правка
  // одного поля переписывает только её — соседние вкладки не задеваются.
  const setField = (k, v) => upd('post_call_actions', {
    ...actions,
    [current]: { ...config, [k]: v },
  });

  return (
    <div className="tab-content form-container">
      <div className="form-section">
        <h3>Post-Call Actions</h3>
        <p className="helper-text">
          What should happen automatically after each call completes? Any number of these
          can run at once — enable the ones you need.
        </p>

        <div className="tabs tabs--sub">
          {ACTION_TYPES.map(t => (
            <button
              key={t.id}
              className={`tab ${current === t.id ? 'tab--active' : ''}`}
              onClick={() => setCurrent(t.id)}
            >
              {t.label}
              {actions[t.id]?.enabled && <span className="tab-dot" title="Enabled" />}
            </button>
          ))}
        </div>

        <div className="toggle-row">
          <label>Enabled</label>
          <input type="checkbox" checked={!!config.enabled}
            onChange={e => setField('enabled', e.target.checked)} />
        </div>

        {current === 'email' && (
          <>
            <div className="form-group">
              <label>Recipient Name</label>
              <input className="input" value={config.name || ''} onChange={e => setField('name', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Email Address</label>
              <input className="input" type="email" value={config.email || ''} onChange={e => setField('email', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Subject</label>
              <input className="input" value={config.subject || ''} onChange={e => setField('subject', e.target.value)}
                placeholder="New call from {caller}" />
            </div>
            <div className="form-group">
              <label>Email Body / Instructions</label>
              <textarea className="input textarea" rows={3} value={config.instructions || ''}
                onChange={e => setField('instructions', e.target.value)}
                placeholder="Include caller name, message summary, callback number..." />
            </div>
          </>
        )}

        {current === 'sms_team' && (
          <>
            <div className="form-group">
              <label>Recipient Phone</label>
              <input className="input" type="tel" value={config.phone || ''}
                onChange={e => setField('phone', e.target.value)} placeholder="0400 000 000" />
            </div>
            <SmsMessageField value={config.message} onChange={v => setField('message', v)}
              placeholder="New call from {caller} - {summary}" />
          </>
        )}

        {current === 'sms_caller' && (
          <>
            <p className="helper-text">
              Sent to the number that called the agent. A withheld or unknown caller id is
              skipped — that shows up on the call as <code>no_caller_number</code>.
            </p>
            <SmsMessageField value={config.message} onChange={v => setField('message', v)}
              placeholder="Thanks for calling! We'll get back to you shortly." />
          </>
        )}

        {current === 'webhook' && (
          <>
            <div className="form-group">
              <label>Webhook URL</label>
              <input className="input" type="url" value={config.url || ''} onChange={e => setField('url', e.target.value)}
                placeholder="https://yoursite.com/webhook" />
            </div>
            <div className="toggle-row">
              <label>Also trigger on failed calls <small>coming soon</small></label>
              <input type="checkbox" checked={false} readOnly disabled />
            </div>
            <div className="form-group">
              <label>Fields to Include</label>
              <p className="helper-text">The conversation id is always sent.</p>
              {WEBHOOK_FIELDS.map(field => (
                <label key={field} className="checkbox-row">
                  <input type="checkbox"
                    checked={config.fields?.includes(field) || false}
                    onChange={e => {
                      const fields = config.fields || [];
                      setField('fields', e.target.checked ? [...fields, field] : fields.filter(f => f !== field));
                    }} />
                  {field}
                </label>
              ))}
            </div>
          </>
        )}

        <button className="btn btn-primary" onClick={onSave} disabled={saving}>
          <Save size={16} /> {saving ? 'Saving...' : 'Save Actions'}
        </button>
      </div>
    </div>
  );
}

function SmsMessageField({ value, onChange, placeholder }) {
  const length = (value || '').length;
  return (
    <div className="form-group">
      <label>Message (max {SMS_MAX_CHARS} chars)</label>
      <textarea className="input textarea" rows={3} maxLength={SMS_MAX_CHARS} value={value || ''}
        onChange={e => onChange(e.target.value)} placeholder={placeholder} />
      <p className="helper-text">
        {length}/{SMS_MAX_CHARS} · placeholders: {'{caller}'} {'{summary}'} {'{duration}'} {'{agent_number}'} {'{date}'}
      </p>
    </div>
  );
}

function EmbedTab({ agent, upd, onSave, saving }) {
  const config = { ...EMBED_DEFAULTS, ...(agent.embed_config || {}) };
  const setField = (k, v) => upd('embed_config', { ...config, [k]: v });

  // Портал и API живут на одном домене (nginx фронта проксирует /api), поэтому
  // адрес скрипта берём из адреса самого кабинета — хардкодить нечего.
  const snippet = `<script src="${window.location.origin}/embed.js" data-agent="${agent.id}" async></script>`;

  const copySnippet = () => {
    navigator.clipboard.writeText(snippet);
    toast.success('Code copied!');
  };

  return (
    <div className="tab-content form-container">
      <div className="form-section">
        <h3>Embed on Your Website</h3>
        <p className="helper-text">
          Paste this into your website's HTML — once per page, anywhere in the body.
          The widget lets visitors talk to this agent straight from the page.
        </p>

        <div className="code-block">
          <pre>{snippet}</pre>
          <button className="btn btn-sm" onClick={copySnippet}>Copy Code</button>
        </div>

        {!config.enabled && (
          <p className="helper-text">
            The widget is currently switched off — the snippet above stays inactive
            until you enable it below and save.
          </p>
        )}

        <p className="helper-text">
          By default the widget floats in the corner of the page. To place it inside a
          specific element, add <code>data-container="#your-element-id"</code> to the tag.
        </p>
      </div>

      <div className="form-section">
        <h3>Widget Appearance</h3>
        <p className="helper-text">
          Saved settings are applied to the widget itself, so they take effect on every
          site where it is embedded — no need to update the snippet.
        </p>

        <div className="toggle-row">
          <label>Enable the widget</label>
          <input type="checkbox" checked={!!config.enabled}
            onChange={e => setField('enabled', e.target.checked)} />
        </div>

        <div className="form-group">
          <label>Theme</label>
          <select className="input" value={config.theme}
            onChange={e => setField('theme', e.target.value)}>
            {EMBED_THEMES.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>

        <div className="form-group">
          <label>Opens as</label>
          <select className="input" value={config.default_state}
            onChange={e => setField('default_state', e.target.value)}>
            {EMBED_STATES.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>

        <div className="form-group">
          <label>Placement</label>
          <select className="input" value={config.position}
            onChange={e => setField('position', e.target.value)}>
            {EMBED_POSITIONS.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
          <p className="helper-text">
            <code>fixed</code> floats above the page, <code>static</code> sits in the
            flow where you place it.
          </p>
        </div>

        <div className="form-group">
          <label>Audio visualiser colour</label>
          <select className="input" value={config.visualizer_color}
            onChange={e => setField('visualizer_color', e.target.value)}>
            {EMBED_VISUALIZER_COLORS.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>

        <EmbedTextField label="Start call button" field="start_call_text"
          value={config.start_call_text} onChange={setField} />
        <EmbedTextField label="Agent thinking text" field="agent_thinking_text"
          value={config.agent_thinking_text} onChange={setField} />
        <EmbedTextField label="Interrupt hint" field="speak_to_interrupt_text"
          value={config.speak_to_interrupt_text} onChange={setField} />

        <div className="form-group">
          <label>Logo URL</label>
          <input className="input" type="url" value={config.logo_icon_url || ''}
            onChange={e => setField('logo_icon_url', e.target.value)}
            placeholder="https://yoursite.com/logo.png" />
          <p className="helper-text">
            Must be an https address — browsers block plain http images on secure pages.
            Leave empty for no logo.
          </p>
        </div>

        <button className="btn btn-primary" onClick={onSave} disabled={saving}>
          <Save size={16} /> {saving ? 'Saving...' : 'Save Embed Settings'}
        </button>
      </div>
    </div>
  );
}

function EmbedTextField({ label, field, value, onChange }) {
  return (
    <div className="form-group">
      <label>{label}</label>
      <input className="input" maxLength={EMBED_MAX_TEXT} value={value || ''}
        onChange={e => onChange(field, e.target.value)} />
    </div>
  );
}
